<template>
  <div>
    <div v-if="loadingStatus === 'loading'">
      <b-container>
        <div class="alert alert-info" style="text-align: center;">
          <h4 style="margin: 1rem;">
            Chargement des réponses en cours...
          </h4>
        </div>
      </b-container>
    </div>

    <div v-if="loadingStatus === 'error' || errormsg">
      <b-container>
        <div class="alert alert-danger" style="text-align: center;">
          <span v-if="errormsg" style="color: #d44" v-html="errormsg"></span>
        </div>
      </b-container>
    </div>

    <div v-if="(test && answers && loadingStatus === 'loaded')">
      <b-container>
        <div class="answers-header mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h2 style="color: #501450; font-weight: 800;">
                Réponses au test : {{ test.name }}
              </h2>
              <p v-if="test.description" class="text-muted">{{ test.description }}</p>
            </div>
            <b-button variant="outline-secondary" @click="$router.go(-1)">
              ← Retour
            </b-button>
          </div>

          <div class="stats-cards mt-3">
            <b-row>
              <b-col md="3">
                <b-card class="text-center">
                  <h4>{{ answers.length }}</h4>
                  <p class="text-muted">Réponses totales</p>
                </b-card>
              </b-col>
              <b-col md="3">
                <b-card class="text-center">
                  <h4>{{ totalQuestions }}</h4>
                  <p class="text-muted">Questions par test</p>
                </b-card>
              </b-col>
              <b-col md="3">
                <b-card class="text-center">
                  <h4>{{ uniqueUsers }}</h4>
                  <p class="text-muted">Utilisateurs uniques</p>
                </b-card>
              </b-col>
              <b-col md="3">
                <b-card class="text-center">
                  <h4>{{ latestAnswerDate }}</h4>
                  <p class="text-muted">Dernière réponse</p>
                </b-card>
              </b-col>
            </b-row>
          </div>
        </div>

        <b-card class="mb-4">
          <b-form-group label="Filtrer par utilisateur">
            <b-form-select v-model="selectedUser" :options="userOptions"></b-form-select>
          </b-form-group>
        </b-card>

        <div v-for="(answer, answerIndex) in filteredAnswers" :key="answer._id" class="mb-4">
          <b-card>
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center">
                <h5>Réponse #{{ answerIndex + 1 }}</h5>
                <div>
                  <span class="badge badge-info mr-2">
                    Utilisateur: {{ (answer && answer.userInfo && answer.userInfo.name) ? answer.userInfo.name : answer.userId }}
                  </span>
                  <span class="badge badge-primary mr-2" v-if="answer && answer.userInfo && answer.userInfo.email">
                    {{ answer.userInfo.email }}
                  </span>
                  <span class="badge badge-secondary">
                    {{ formatDate(answer.metadata && answer.metadata.creation_date) }}
                  </span>
                </div>
              </div>
            </b-card-header>

            <b-card-body>
              <div v-if="answer.testType === 'normatif'">
                <div v-for="(group, groupIndex) in answer.answers" :key="groupIndex" class="mb-4">
                  <h6 class="text-primary">{{ group.groupName }}</h6>

                  <div v-for="(item, itemIndex) in group.items" :key="itemIndex" class="ml-3 mb-3">
                    <h6 class="text-secondary">{{ item.itemName }}</h6>

                    <div v-for="(question, questionIndex) in item.questions" :key="questionIndex" class="ml-3 mb-2">
                      <div class="question-response">
                        <strong>Question:</strong> {{ question.questionContent }}
                        <br>
                        <strong>Réponse:</strong>
                        <span v-if="question.questionType === 'choice'">
                          {{ (question.choices && question.choices[question.userAnswer]) || 'Non spécifié' }}
                        </span>
                        <span v-else-if="question.questionType === 'score'">
                          {{ (question.scoresValues && question.scoresValues[question.userAnswer]) || 'Non spécifié' }}
                        </span>
                        <span v-else>
                          {{ question.userAnswer || 'Non spécifié' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else>
                <p class="text-muted">Format de réponse non reconnu</p>
              </div>
            </b-card-body>
          </b-card>
        </div>

        <div v-if="filteredAnswers.length > 10" class="d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredAnswers.length"
            :per-page="10"
            aria-controls="answers-table"
          ></b-pagination>
        </div>
      </b-container>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NormatifAnswers',
  data() {
    return {
      loadingStatus: 'loading',
      test: null,
      answers: [],
      errormsg: null,
      testId: null,
      selectedUser: null,
      currentPage: 1
    };
  },
  computed: {
    totalQuestions() {
      if (!this.test || !this.test.itemGroups) return 0;
      return this.test.itemGroups.reduce((total, group) => {
        return total + group.items.reduce((itemTotal, item) => {
          return itemTotal + (item.questions ? item.questions.length : 0);
        }, 0);
      }, 0);
    },
    uniqueUsers() {
      const users = new Set(this.answers.map(a => a.userId));
      return users.size;
    },
    latestAnswerDate() {
      if (!this.answers.length) return 'Aucune';
      const latest = this.answers.reduce((latest, current) => {
        return new Date(current.metadata.creation_date) > new Date(latest.metadata.creation_date) ? current : latest;
      });
      return this.formatDate(latest.metadata.creation_date);
    },
    userOptions() {
      const users = [...new Set(this.answers.map(a => a.userId))];
      return [
        { value: null, text: 'Tous les utilisateurs' },
        ...users.map(user => {
          const answer = this.answers.find(a => a.userId === user);
          const displayName = (answer && answer.userInfo) ? (answer.userInfo.name || answer.userInfo.email) : user;
          return { value: user, text: displayName };
        })
      ];
    },
    filteredAnswers() {
      let filtered = this.answers;
      if (this.selectedUser) {
        filtered = filtered.filter(a => a.userId === this.selectedUser);
      }
      return filtered;
    }
  },
  methods: {
    async loadData() {
      try {
        this.loadingStatus = 'loading';
        this.testId = this.$route.params.testId;

        if (!this.testId) {
          throw new Error('ID du test manquant');
        }

        const testResponse = await fetch(`/api/tests/${this.testId}`);
        if (!testResponse.ok) {
          throw new Error('Test non trouvé');
        }
        this.test = await testResponse.json();

        // Essayer d'abord de charger toutes les réponses (admin)
        let answersResponse = await fetch(`/api/tests/${this.testId}/all-answers`);
        if (answersResponse.status === 403) {
          // Si pas admin, charger seulement les réponses de l'utilisateur connecté
          answersResponse = await fetch(`/api/tests/${this.testId}/answers`);
        }

        if (!answersResponse.ok) {
          throw new Error('Erreur lors du chargement des réponses');
        }
        this.answers = await answersResponse.json();

        this.loadingStatus = 'loaded';
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        this.errormsg = 'Erreur lors du chargement: ' + error.message;
        this.loadingStatus = 'error';
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  mounted() {
    this.loadData();
  }
};
</script>

<style scoped>
.answers-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
  border-radius: 10px;
  border: 1px solid #dee2e6;
}

.stats-cards .card {
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stats-cards h4 {
  color: #501450;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.question-response {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 3px solid #501450;
  margin-bottom: 0.5rem;
}

.badge {
  font-size: 0.8rem;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.badge-primary {
  background-color: #007bff;
  color: white;
}
</style>
